import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";

export default function ReportsPage() {
  const [diagnoses, setDiagnoses] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => { (async () => {
    const [d, dp, s] = await Promise.all([
      api.get("/reports/diagnoses"),
      api.get("/reports/doctor-performance"),
      api.get("/dashboard/stats"),
    ]);
    setDiagnoses(d.data); setDoctors(dp.data); setStats(s.data);
  })(); }, []);

  return (
    <div className="space-y-5 fade-up" data-testid="reports-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Insights across your clinic operations</p></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader><CardTitle>Top diagnoses</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={diagnoses} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis dataKey="diagnosis" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} width={80} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {diagnoses.length === 0 && <div className="text-center text-muted-foreground text-sm py-6">No diagnoses recorded yet.</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Doctor performance</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={doctors}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="appointments" radius={[6, 6, 0, 0]}>
                    {doctors.map((d, i) => <Cell key={d.id || d.name || i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Appointment status distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={stats?.appointments_by_status || []} dataKey="count" nameKey="status" cx="50%" cy="50%"
                    outerRadius={100} innerRadius={50} paddingAngle={2}>
                    {(stats?.appointments_by_status || []).map((s, i) => <Cell key={s.status || i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
